import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Square, Upload, Sparkles, Loader2, PenLine } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });
const CHUNK = 5 * 1024 * 1024;

function pickMime() {
  const opts = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  try { return opts.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || ''; } catch { return ''; }
}

export default function CompleteVisionBox({ vision, base, q, value, onChange, onSaveText, onServerUpdate, onPrefill, prefilling, hidePrefill = false }) {
  const [phase, setPhase] = useState('');
  const [pct, setPct] = useState(0);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const tickRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(tickRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const poll = () => {
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      try {
        const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}${q}`);
        if (r.data.transcribe_status === 'done' || r.data.transcribe_status === 'failed' || Date.now() - started > 15 * 60 * 1000) {
          clearInterval(pollRef.current); setPhase('');
          if (r.data.transcribe_status === 'done') { onServerUpdate(r.data); toast.success('Transcribed and added to your vision'); }
          else toast.error(r.data.transcribe_error || 'Transcription is taking too long, please try again.');
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const sendAudio = async (blob) => {
    setPhase('uploading'); setPct(0);
    try {
      const total = Math.max(1, Math.ceil(blob.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('chunk', blob.slice(i * CHUNK, (i + 1) * CHUNK), 'audio');
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        await ax.post(`${BACKEND}${base}/${vision.vision_id}/audio-chunk${q}`, fd);
        setPct(Math.round(((i + 1) / total) * 100));
      }
      setPhase('transcribing');
      await ax.post(`${BACKEND}${base}/${vision.vision_id}/transcribe${q}`);
      poll();
    } catch (e) { setPhase(''); toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size > 0) sendAudio(blob);
      };
      mr.start(1000);
      recRef.current = mr;
      setRecording(true); setSecs(0);
      tickRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch { toast.error('Microphone access was blocked. Allow it in your browser, or upload an audio file instead.'); }
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); clearInterval(tickRef.current); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) sendAudio(f);
    if (fileRef.current) fileRef.current.value = '';
  };

  const busy = !!phase;
  const label = phase === 'uploading' ? `Uploading… ${pct}%` : phase === 'transcribing' ? 'Transcribing your recording… (about 1–2 min)' : '';
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  return (
    <div className="bg-white border-2 border-[#1D4ED8]/20 rounded-2xl p-5 mb-5" data-testid="complete-vision-box">
      <div className="flex items-center gap-2 mb-1"><PenLine size={16} className="text-[#F97316]" /><h3 className="font-black text-base text-[#0A0F1C]">Your Complete Vision</h3></div>
      <p className="text-xs text-[#64748B] mb-3">Tell us everything about the website you want: who it's for, what it should say and do, how it should feel. Type it, record your voice, upload an audio file, or pull it from your 7-Stage Jumpstart / Nonprofit Accelerator.</p>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} onBlur={(e) => onSaveText(e.target.value)} rows={8}
        placeholder="e.g. I want a warm, modern site for my coaching practice that helps new moms feel seen. The homepage should..."
        data-testid="complete-vision-text"
        className="w-full px-4 py-3 rounded-xl border border-[#E2E8F0] text-sm leading-relaxed focus:border-[#1D4ED8] outline-none resize-y" />
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {!recording ? (
          <button type="button" onClick={startRec} disabled={busy} data-testid="vision-record-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-[#0A0F1C] text-white hover:bg-[#1e293b] disabled:opacity-50 transition-colors">
            <Mic size={15} /> Record my voice
          </button>
        ) : (
          <button type="button" onClick={stopRec} data-testid="vision-record-stop-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors">
            <Square size={13} fill="white" /> Stop & transcribe <span className="font-mono text-xs opacity-90 ml-1">{mmss}</span>
          </button>
        )}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || recording} data-testid="vision-audio-upload-btn"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8] disabled:opacity-50 transition-colors">
          <Upload size={15} /> Upload audio
        </button>
        <input ref={fileRef} type="file" accept="audio/*,video/mp4,video/webm,.m4a,.mp3,.wav" onChange={onFile} className="hidden" data-testid="vision-audio-input" />
        {!hidePrefill && <button type="button" onClick={onPrefill} disabled={prefilling || busy} data-testid="vision-prefill-btn"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#EFF6FF] disabled:opacity-50 transition-colors">
          {prefilling ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Pull from Jumpstart / Accelerator
        </button>}
        {(busy || recording) && (
          <span className="text-xs font-semibold text-[#1D4ED8] inline-flex items-center gap-1.5" data-testid="vision-audio-status">
            {recording ? <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> : <Loader2 size={13} className="animate-spin" />}
            {recording ? 'Recording…' : label}
          </span>
        )}
      </div>
    </div>
  );
}
